// ============================================================================
// Plugin algèbre — vérification d'une étape (lot 2 de la refonte,
// docs/moteur/REFONTE_MOTEUR.md, « checkStep »). Dans cet ordre :
//   1. une application de règle reconnue (forme de départ → forme d'arrivée,
//      préconditions décidées) → valid + ruleId ; précondition inconnue →
//      conditional (conditions) ou branches (a > 0 | a < 0 | a = 0) ;
//   2. sinon, identité / équivalence PROUVÉE (forme normale exacte, ou étude
//      exacte complète en une inconnue) → valid ;
//   3. sinon, différence PROUVÉE par un contre-exemple exact → invalid ;
//   4. sinon unknown / unsupported. Jamais un verdict deviné.
// ============================================================================

import { conditionOn, decide, factsFrom, isNonzero, type Facts } from '../../conditions';
import * as Q from '../../exact';
import type { Rat } from '../../exact';
import { matchRuleStep, type RuleStepMatch } from '../../match';
import {
  compareTerms,
  evalExact,
  evalFloat,
  NormalFail,
  normalize,
  polyDivExact,
  polyFloat,
  polyHasOpaque,
  polyIsConstant,
  polyIsZero,
  polyRat,
  polyScale,
  polySymbols,
  polyToTerm,
  rfRat,
  rfSimplify,
  rfToTerm,
  toRatFn,
  type Poly,
  type RatFn,
} from '../../normal';
import type { LoadedRule } from '../../rules/loader';
import {
  add,
  canon,
  div,
  fn,
  mul,
  num,
  mapStatement,
  parseStatement,
  relationsIn,
  sameTerm,
  statementSymbols,
  statementText,
  sub,
  substitute,
  sym,
  symbolsOf,
  termKey,
  termText,
  type Rel,
  type Sorts,
  type Statement,
  type Term,
} from '../../term';
import {
  unknownCheck,
  unsupportedCheck,
  type Branch,
  type Condition,
  type Goal,
  type StepCheck,
  type StepRelation,
} from '../../types';
import { atText as trigAtText, compareTrig, homogeneous, trigDomainOf } from '../../trig';
import { sampleEnvs, univariateSolutions } from '../../witness';

export interface AlgebraCheckContext {
  rules: readonly LoadedRule[];
  hypotheses?: readonly Condition[];
  /** sorte de chaque lettre (absente : réel) */
  sorts?: Sorts;
  goal?: Goal;
  /** changement de variable déclaré : x = y + 3 (exemple 11.7) */
  substitutions?: readonly { name: string; value: string }[];
  /** valeur approchée acceptée, à cette précision (exemple 11.9) */
  approximation?: { places: number };
  deadline?: number;
  now?: () => number;
  /** essais d'appariement au plus */
  fuel?: number;
  /** branches de cas autorisées (une seule profondeur) */
  branches?: boolean;
  /**
   * angles mesurés EN DEGRÉS (moteur v21) : « acute » ]0° ; 90°[ (triangle rectangle),
   * « angle » ]0° ; 180°[ (angle d'un triangle). cos, sin, tan lisent alors leur argument
   * en degrés, « 60° » hors d'un cos vaut 60, et arccos, arcsin, arctan rendent des degrés.
   */
  angles?: Readonly<Record<string, AngleDomain>>;
  /**
   * équation trigonométrique en radians (moteur v24) : l'inconnue (`goal.target`) est un
   * réel ; les autres lettres déclarées entières (k) décrivent des familles
   * (« x = π ÷ 3 + 2kπ ») ; `domain` (« −π < x ≤ π ») : on compare les solutions sur cet
   * intervalle, sinon sur ℝ.
   */
  trig?: { domain?: Statement | null };
}
export type AngleDomain = 'acute' | 'angle';

interface Ctx {
  rules: readonly LoadedRule[];
  hyps: Condition[];
  facts: Facts;
  sorts: Sorts;
  goal?: Goal;
  approximation?: { places: number };
  deadline: number;
  now: () => number;
  fuel: number;
  branches: boolean;
  angles: Readonly<Record<string, AngleDomain>>;
  trig?: { domain?: Statement | null };
}

/* ------------------------------ résultats ------------------------------ */

const valid = (
  relation: StepRelation,
  code: string,
  params?: Record<string, string>,
  proof: StepCheck['proof'] = 'exact',
): StepCheck => ({
  status: 'valid',
  relation,
  proof,
  explanation: { code, ...(params && { params }) },
});
const invalid = (
  code: string,
  params?: Record<string, string>,
  proof: StepCheck['proof'] = 'exact',
): StepCheck => ({
  status: 'invalid',
  relation: 'none',
  proof,
  explanation: { code, ...(params && { params }) },
});
const clip = (s: string): string => (s.length > 400 ? s.slice(0, 397) + '…' : s);

/* ------------------------------ entrée ------------------------------ */

/** Vérifie le passage de la ligne `from` à la ligne `to`. */
export function checkAlgebraStep(from: string, to: string, opts: AlgebraCheckContext): StepCheck {
  const a = parseStatement(from);
  const b = parseStatement(to);
  if (!a.ok || !b.ok) {
    const reason = (!a.ok ? a.reason : !b.ok ? b.reason : 'unparsable') as string;
    return unsupportedCheck(reason === 'unparsable' ? 'unparsable' : 'unsupported_notation');
  }
  return checkAlgebraStatements(a.s, b.s, opts);
}

/**
 * Même vérification, sur des énoncés déjà lus (lot 3 : l'adaptateur du moteur actuel
 * construit les énoncés lui-même, sans repasser par le texte).
 */
export function checkAlgebraStatements(
  from: Statement,
  to: Statement,
  opts: AlgebraCheckContext,
): StepCheck {
  const now = opts.now ?? (() => Date.now());
  const hyps = [...(opts.hypotheses ?? [])];
  const sorts = opts.sorts ?? {};
  const ctx: Ctx = {
    rules: opts.rules,
    hyps,
    facts: factsFrom(hyps, sorts),
    sorts,
    goal: opts.goal,
    approximation: opts.approximation,
    deadline: opts.deadline ?? now() + 50,
    now,
    fuel: opts.fuel ?? 4000,
    branches: opts.branches ?? true,
    angles: opts.angles ?? {},
    ...(opts.trig ? { trig: opts.trig } : {}),
  };
  let prev = from;
  let next = to;
  // changement de variable : tout est exprimé dans les nouvelles variables
  if (opts.substitutions?.length) {
    const map: Record<string, Term> = {};
    for (const s of opts.substitutions) {
      const v = parseStatement(s.value);
      if (!v.ok || v.s.k !== 'expr') return unsupportedCheck('bad_substitution');
      map[s.name] = v.s.e;
    }
    prev = mapStatement(prev, (t) => substitute(t, map));
    next = mapStatement(next, (t) => substitute(t, map));
  }
  // angles en degrés : tout se lit dans le même monde (moteur v21)
  if (Object.keys(ctx.angles).length) {
    prev = mapStatement(prev, inDegrees);
    next = mapStatement(next, inDegrees);
    return paramsInDegrees(checkStatements(prev, next, ctx), ctx);
  }
  return checkStatements(prev, next, ctx);
}

/* ------------------------------ angles en degrés (moteur v21) ------------------------------ */

const TRIG = new Set(['cos', 'sin', 'tan']);
/** « 60° » hors d'un cos vaut 60 (une mesure en degrés) ; le degré n'existe plus nulle part */
function withoutDeg(t: Term): Term {
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withoutDeg) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withoutDeg(t.a), b: withoutDeg(t.b) };
    case 'fn':
      return t.f === 'deg' ? withoutDeg(t.a) : { ...t, a: withoutDeg(t.a) };
    default:
      return t;
  }
}
/** Dans une feuille d'angles, cos, sin et tan lisent leur argument en degrés. */
export function inDegrees(t: Term): Term {
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(inDegrees) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: inDegrees(t.a), b: inDegrees(t.b) };
    case 'fn':
      if (t.f === 'deg') return inDegrees(t.a);
      if (TRIG.has(t.f)) return fn(t.f, fn('deg', withoutDeg(t.a)));
      return { ...t, a: inDegrees(t.a) };
    default:
      return t;
  }
}

/** cos(Â × π ÷ 180) se relit cos(Â) : l'élève écrit ses angles en degrés, sans « ° » */
function asWritten(t: Term, ctx: Ctx): Term {
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map((c) => asWritten(c, ctx)) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: asWritten(t.a, ctx), b: asWritten(t.b, ctx) };
    case 'fn': {
      if (t.f === 'deg') return asWritten(t.a, ctx);
      if (!TRIG.has(t.f)) return { ...t, a: asWritten(t.a, ctx) };
      const n = normalize(div(mul(t.a, num(180)), sym('π')), { sorts: ctx.sorts });
      return n.ok ? fn(t.f, rfToTerm(rfSimplify(n.rf))) : t;
    }
    default:
      return t;
  }
}
/** Les valeurs d'une explication (terme ajouté, facteur, solution) écrites en degrés. */
function paramsInDegrees(c: StepCheck, ctx: Ctx): StepCheck {
  const params = c.explanation?.params;
  if (!params) return c;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    // seules les écritures mathématiques (pas le libellé de la règle) qui parlent d'angle
    if (k === 'label' || !/\b(cos|sin|tan)\b|°|π/.test(v)) {
      out[k] = v;
      continue;
    }
    const p = parseStatement(v);
    out[k] = p.ok ? statementText(mapStatement(p.s, (t) => asWritten(t, ctx))) : v;
  }
  return { ...c, explanation: { ...c.explanation!, params: out } };
}

/** Les cos(x°), sin(x°), tan(x°) d'une expression (x l'angle cherché). */
function trigNodesOf(t: Term, x: string, out: Term[] = []): Term[] {
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => trigNodesOf(c, x, out));
      break;
    case 'div':
    case 'pow':
    case 'binom':
      trigNodesOf(t.a, x, out);
      trigNodesOf(t.b, x, out);
      break;
    case 'fn':
      if (TRIG.has(t.f) && symbolsOf(t.a).has(x)) out.push(t);
      else trigNodesOf(t.a, x, out);
      break;
    default:
      break;
  }
  return out;
}
function replaceTerm(t: Term, key: string, by: Term): Term {
  if (termKey(t) === key) return by;
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map((c) => replaceTerm(c, key, by)) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: replaceTerm(t.a, key, by), b: replaceTerm(t.b, key, by) };
    case 'fn':
      return { ...t, a: replaceTerm(t.a, key, by) };
    default:
      return t;
  }
}
const ANGLE_VAR = '§angle';
const ANGLE_MAX: Record<AngleDomain, number> = { acute: 90, angle: 180 };

/**
 * Solutions exactes d'une équation en un angle x (mesuré en degrés, dans son domaine) :
 * « x = constante », ou une équation en cos(x°), sin(x°) ou tan(x°) (une seule de ces
 * écritures) résolue en c puis inversée : x = arccos(c)… null si l'étude n'est pas sûre.
 */
function angleSolutions(
  s: Statement,
  x: string,
  dom: AngleDomain,
  ctx: Ctx,
): { roots: Term[]; trig: string | null } | null {
  if (s.k !== 'rel' || s.rel !== '=') return null;
  let e = sub(s.l, s.r);
  const max = ANGLE_MAX[dom];
  const inside = (v: number): boolean => v > 1e-9 && v < max - 1e-9;
  let nodes = trigNodesOf(e, x);
  // a sin(x) + b cos(x) = 0 (moteur v24) : diviser par cos(x), tan(x) = −b ÷ a
  const keys = [...new Set(nodes.map(termKey))];
  if (keys.length === 2) {
    const n1 = nodes.find((n) => termKey(n) === keys[0])!;
    const n2 = nodes.find((n) => termKey(n) === keys[1])!;
    if (n1.k !== 'fn' || n2.k !== 'fn' || termKey(n1.a) !== termKey(n2.a)) return null;
    const [s, c] = n1.f === 'sin' ? [n1, n2] : [n2, n1];
    if (s.f !== 'sin' || c.f !== 'cos') return null;
    const h = homogeneous(e, s, c, ctx.sorts);
    if (!h) return null;
    const a = normalize(h.A, { sorts: ctx.sorts });
    if (!a.ok || polyIsZero(a.rf.n)) return null;
    const tanNode = fn('tan', s.a);
    e = add(tanNode, div(h.B, h.A));
    nodes = [tanNode];
  }
  if (!nodes.length) {
    const n = normalize(e, { sorts: ctx.sorts });
    if (!n.ok || polyIsConstant(n.rf.n)) return null;
    const sol = univariateSolutions({ n: n.rf.n, d: UNIT }, x);
    if (!sol || !sol.complete) return null;
    return { roots: sol.roots.filter((r) => inside(evalFloat(r, {}))), trig: null };
  }
  const key = termKey(nodes[0]!);
  if (nodes.some((t) => termKey(t) !== key)) return null;
  const node = nodes[0]!;
  if (node.k !== 'fn' || node.a.k !== 'fn' || node.a.f !== 'deg') return null;
  const arg = node.a.a;
  if (arg.k !== 'sym' || arg.n !== x) return null;
  const inU = replaceTerm(e, key, sym(ANGLE_VAR));
  if (symbolsOf(inU).has(x)) return null;
  const n = normalize(inU, { sorts: ctx.sorts });
  if (!n.ok || polyIsConstant(n.rf.n)) return null;
  const sol = univariateSolutions({ n: n.rf.n, d: UNIT }, ANGLE_VAR);
  if (!sol || !sol.complete) return null;
  const roots: Term[] = [];
  for (const c of sol.roots) {
    const v = evalFloat(c, {});
    if (!Number.isFinite(v)) return null;
    if (node.f === 'cos') {
      if (v > -1 + 1e-12 && v < 1 - 1e-12) roots.push(fn('arccos', c));
    } else if (node.f === 'sin') {
      if (v <= 1e-12 || v > 1 + 1e-12) continue;
      if (Math.abs(v - 1) <= 1e-12) roots.push(num(90));
      else {
        roots.push(fn('arcsin', c));
        if (dom === 'angle') roots.push(sub(num(180), fn('arcsin', c)));
      }
    } else if (Math.abs(v) > 1e-12) {
      roots.push(v > 0 ? fn('arctan', c) : add(num(180), fn('arctan', c)));
    }
  }
  return { roots: roots.filter((r) => inside(evalFloat(r, {}))), trig: node.f };
}
/** même valeur (forme normale), valeurs séparées, ou indécis */
function sameAngle(a: Term, b: Term, ctx: Ctx): boolean | null {
  const d = normalize(sub(a, b), { sorts: ctx.sorts });
  if (d.ok && polyIsZero(d.rf.n)) return true;
  const fa = evalFloat(a, {});
  const fb = evalFloat(b, {});
  if (!Number.isFinite(fa) || !Number.isFinite(fb)) return null;
  return Math.abs(fa - fb) > 1e-9 ? false : null;
}
/** Une équation en un angle (moteur v21) : ensembles de solutions comparés exactement. */
function angleStudy(
  prev: Statement,
  next: Statement,
  x: string,
  dom: AngleDomain,
  ctx: Ctx,
): StepCheck | null {
  const p = angleSolutions(prev, x, dom, ctx);
  const n = angleSolutions(next, x, dom, ctx);
  if (!p || !n || (!p.trig && !n.trig)) return null;
  // une solution s'écrit avec sa valeur exacte quand elle en a une (arccos(1 ÷ 2) = 60)
  const shown = (t: Term): string => {
    const v = normalize(t, { sorts: ctx.sorts });
    return termText(
      v.ok && polyIsConstant(v.rf.n) && polyIsConstant(v.rf.d) ? rfToTerm(v.rf) : canon(t),
    );
  };
  const member = (t: Term, set: Term[]): boolean | null => {
    let undecided = false;
    for (const u of set) {
      const same = sameAngle(t, u, ctx);
      if (same) return true;
      if (same === null) undecided = true;
    }
    return undecided ? null : false;
  };
  for (const t of p.roots) {
    const m = member(t, n.roots);
    if (m === null) return null;
    if (!m) return invalid('lost_solution', { at: `${x} = ${shown(t)}` });
  }
  for (const t of n.roots) {
    const m = member(t, p.roots);
    if (m === null) return null;
    if (!m) return valid('implies', 'extra_solutions', { at: `${x} = ${shown(t)}` });
  }
  // retrouver l'angle : de cos(x) = c à x = arccos(c)
  if (p.trig && !n.trig) return valid('equivalent', 'inverse_trig', { f: p.trig });
  return valid('equivalent', 'equivalent');
}

/** Lettres supposées nulles (branche « a = 0 ») : remplacées par 0. */
function zeroed(s: Statement, ctx: Ctx): Statement {
  const map: Record<string, Term> = {};
  for (const [name, set] of ctx.facts.sign)
    if (set.zero && !set.pos && !set.neg) map[name] = num(0);
  return Object.keys(map).length ? mapStatement(s, (t) => substitute(t, map)) : s;
}

function checkStatements(prev0: Statement, next0: Statement, ctx: Ctx): StepCheck {
  const prev = zeroed(prev0, ctx);
  const next = zeroed(next0, ctx);
  // une ligne qui divise par zéro est fausse, quelle que soit la précédente
  const dz = divisionByZero(next, ctx);
  if (dz) return invalid(dz);
  if (divisionByZero(prev, ctx)) return unknownCheck('previous_line_undefined');
  if (ctx.now() > ctx.deadline) return unknownCheck('budget_exhausted');

  if (Object.values(ctx.sorts).includes('matrix')) return checkMatrixStep(prev, next, ctx);

  // 1. règle reconnue
  const byRule = checkByRules(prev, next, ctx);
  if (byRule) return byRule;
  if (ctx.now() > ctx.deadline) return unknownCheck('budget_exhausted');

  // 2–3. forme normale, étude exacte, contre-exemple
  if (prev.k === 'expr' && next.k === 'expr') return checkExpressions(prev.e, next.e, ctx);
  if (prev.k === 'expr' || next.k === 'expr') return unknownCheck('statement_kind_changed');
  const r = checkRelations(prev, next, ctx);
  return r.status === 'unknown' ? (withoutRoots(prev, next, ctx) ?? r) : r;
}

/**
 * « S = √E » avec S ≥ 0 équivaut à « S² = E » (lot 3 bis : « c = √A » depuis « A = c² »,
 * c une longueur). S au signe inconnu : la même étape, à condition que S ≥ 0.
 */
function withoutRoots(prev: Statement, next: Statement, ctx: Ctx): StepCheck | null {
  const conds: Condition[] = [];
  let weak = false;
  const squared = (s: Statement, isNext: boolean): Statement | null => {
    if (s.k !== 'rel' || s.rel !== '=') return null;
    const [root, other, rootLeft] =
      s.l.k === 'fn' && s.l.f === 'sqrt'
        ? [s.l, s.r, true]
        : s.r.k === 'fn' && s.r.f === 'sqrt'
          ? [s.r, s.l, false]
          : [null, null, false];
    if (!root || !other || root.k !== 'fn') return null;
    const c = conditionOn('nonnegative', other);
    const d = decide(c, ctx.facts);
    if (d === 'false') return null;
    // élever au carré la ligne de départ est toujours permis (elle IMPLIQUE son carré) ;
    // revenir de « S² = E » à « S = √E » demande S ≥ 0
    if (d !== 'true') {
      if (isNext) conds.push(c);
      else weak = true;
    }
    const sq: Term = { k: 'pow', a: other, b: num(2) };
    return rootLeft
      ? { k: 'rel', rel: '=', l: root.a, r: sq }
      : { k: 'rel', rel: '=', l: sq, r: root.a };
  };
  const p2 = squared(prev, false);
  const n2 = squared(next, true);
  if (!p2 && !n2) return null;
  const r0 = checkRelations(p2 ?? prev, n2 ?? next, { ...ctx, branches: false });
  if (r0.status !== 'valid') return null;
  const r: StepCheck = weak && r0.relation === 'equivalent' ? { ...r0, relation: 'implies' } : r0;
  if (!conds.length) return { ...r, explanation: { code: 'square_root' } };
  return { ...r, status: 'conditional', conditions: conds, explanation: { code: 'square_root' } };
}

function divisionByZero(s: Statement, ctx: Ctx): string | null {
  const terms = s.k === 'expr' ? [s.e] : relationsIn(s).flatMap((i) => [i.l, i.r]);
  for (const t of terms) {
    const n = normalize(t, { sorts: ctx.sorts });
    if (!n.ok && (n.code === 'division_by_zero' || n.code === 'undefined_value')) return n.code;
  }
  return null;
}

/* ------------------------------ 1. règles ------------------------------ */

function appliesTo(rule: LoadedRule, prev: Statement, next: Statement): boolean {
  if (rule.appliesTo === 'expression')
    return prev.k !== 'or' && next.k !== 'or' && prev.k !== 'set' && next.k !== 'set';
  if (prev.k !== 'rel' || next.k !== 'rel') return false;
  const isEq = (r: Rel): boolean => r === '=';
  return rule.appliesTo === 'equation'
    ? isEq(prev.rel) && isEq(next.rel)
    : !isEq(prev.rel) && !isEq(next.rel);
}

/**
 * Complexité d'un appariement : on préfère « diviser par 3 » à « multiplier par 1/3 »,
 * et un nombre introduit (k = −1) à une expression introduite (c = x + 3).
 */
function bindingWeight(m: RuleStepMatch): number {
  const intro = new Set(
    m.rule.directions.find((d) => d.from === m.from && d.to === m.to)?.introduces,
  );
  let w = 0;
  for (const [name, t] of Object.entries(m.bindings)) {
    const s = termText(t);
    w += s.length + (s.includes('÷') ? 6 : 0) + (s.startsWith('−') ? 3 : 0);
    if (intro.has(name) && statementSymbols({ k: 'expr', e: t }).size) w += 20;
  }
  return w + m.rule.cost * 10;
}

/** Lettres qui sont l'INCONNUE de l'étape : une condition sur elles n'est pas une hypothèse. */
function unknownsOf(prev: Statement, next: Statement, ctx: Ctx): Set<string> {
  if (ctx.goal?.target) return new Set([ctx.goal.target]);
  const all = new Set([...statementSymbols(prev), ...statementSymbols(next)]);
  return all.size === 1 ? all : new Set();
}
function conditionTouches(c: Condition, names: Set<string>): boolean {
  return c.of.some((t) => {
    const p = parseStatement(t);
    return p.ok && [...statementSymbols(p.s)].some((n) => names.has(n));
  });
}

interface Candidate {
  m: RuleStepMatch;
  /** préconditions + conditions de domaine des valeurs introduites */
  conds: Condition[];
  decisions: ReturnType<typeof decide>[];
}

function checkByRules(prev: Statement, next: Statement, ctx: Ctx): StepCheck | null {
  const cands: Candidate[] = [];
  const vanishing = vanishingOn(prev, ctx);
  for (const rule of ctx.rules) {
    if (!appliesTo(rule, prev, next)) continue;
    for (const m of matchRuleStep(rule, prev, next, { sorts: ctx.sorts, fuel: ctx.fuel })) {
      const conds = [...m.conditions, ...domainConditions(m, ctx)];
      const decisions = conds.map((c) =>
        vanishing && zeroUnder(c, vanishing, ctx) ? 'false' : decide(c, ctx.facts),
      );
      if (decisions.includes('false')) continue;
      cands.push({ m, conds, decisions });
    }
    if (ctx.now() > ctx.deadline) break;
  }
  if (!cands.length) return null;
  const sure = cands.filter((c) => c.decisions.every((d) => d === 'true'));
  if (sure.length) {
    const best = sure.sort((x, y) => bindingWeight(x.m) - bindingWeight(y.m))[0]!;
    return ruleCheck(best.m, prev, next);
  }
  // préconditions non décidées : la règle dit quoi faire — sauf si elles portent sur
  // l'inconnue elle-même (« diviser par x » perd peut-être x = 0 : à l'étude exacte de trancher)
  const unknowns = unknownsOf(prev, next, ctx);
  // une règle lue en miroir ne sert qu'avec des conditions établies (sinon l'étude exacte
  // dit mieux : « b = √a → b² = a » est une implication)
  const usable = cands.filter(
    (c) =>
      !c.m.mirrored &&
      !c.conds.some((cond, i) => c.decisions[i] !== 'true' && conditionTouches(cond, unknowns)),
  );
  if (!usable.length) return null;
  const best = usable.sort((x, y) => bindingWeight(x.m) - bindingWeight(y.m))[0]!;
  const open = best.conds.filter((_, i) => best.decisions[i] !== 'true');
  const how = best.m.rule.onUnknown ?? 'conditional';
  if (how === 'refuse') return null;
  if (how === 'branch' && ctx.branches) {
    const br = branchOn(open[0]!, prev, next, ctx);
    if (br) return br;
  }
  return {
    ...ruleCheck(best.m, prev, next),
    status: 'conditional',
    conditions: open,
  };
}

/**
 * Polynôme qui s'annule dès que la ligne de départ est vraie (« 20 − 4x = 0 » → 20 − 4x),
 * ou null. Lot 3 : sans lui, « diviser par (4x − 20) ÷ (…) » passait pour une étape
 * conditionnelle, alors que ce diviseur est nul sur toutes les solutions de départ.
 */
function vanishingOn(prev: Statement, ctx: Ctx): Poly | null {
  if (prev.k !== 'rel' || prev.rel !== '=') return null;
  const n = normalize(sub(prev.l, prev.r), { sorts: ctx.sorts });
  return n.ok && !polyIsConstant(n.rf.n) ? n.rf.n : null;
}
/** « non nul » demandé d'un multiple de ce qui s'annule au départ : toujours faux. */
function zeroUnder(c: Condition, vanishing: Poly, ctx: Ctx): boolean {
  if (c.kind !== 'nonzero' || c.of.length !== 1) return false;
  const p = parseStatement(c.of[0]!);
  if (!p.ok || p.s.k !== 'expr') return false;
  const n = normalize(p.s.e, { sorts: ctx.sorts });
  return n.ok && !polyIsZero(n.rf.n) && polyDivExact(n.rf.n, vanishing) !== null;
}

/** Une valeur introduite avec un dénominateur variable n'a de sens que s'il est non nul. */
function domainConditions(m: RuleStepMatch, ctx: Ctx): Condition[] {
  const out: Condition[] = [];
  const intro = new Set(
    m.rule.directions.find((d) => d.from === m.from && d.to === m.to)?.introduces,
  );
  for (const [name, t] of Object.entries(m.bindings)) {
    if (!intro.has(name)) continue;
    const n = normalize(t, { sorts: ctx.sorts });
    if (n.ok && polyRat(n.rf.d) === null) out.push(conditionOn('nonzero', polyToTerm(n.rf.d)));
  }
  return out;
}

function ruleCheck(m: RuleStepMatch, prev: Statement, next: Statement): StepCheck {
  const params: Record<string, string> = { label: clip(m.rule.label) };
  for (const [k, v] of Object.entries(m.bindings).slice(0, 6)) params[`v_${k}`] = clip(termText(v));
  return {
    status: 'valid',
    relation: m.rule.relation,
    proof: 'rule',
    ruleId: m.rule.qid,
    explanation: { code: 'rule', params },
    trace: [
      {
        from: clip(statementText(prev)),
        to: clip(statementText(next)),
        relation: m.rule.relation,
        ruleId: m.rule.qid,
      },
    ],
  };
}

/* ------------------------------ branches ------------------------------ */

/** Lettre seule (à un coefficient et une puissance impaire près) sur laquelle brancher. */
function branchVariable(t: Term, ctx: Ctx): Term {
  const n = normalize(t, { sorts: ctx.sorts });
  if (!n.ok) return t;
  for (const p of [n.rf.n, n.rf.d]) {
    if (polyIsConstant(p)) continue;
    const terms = [...p.t.values()];
    if (terms.length !== 1) return t;
    const f = terms[0]!.m;
    if (f.length !== 1 || f[0]!.a.kind !== 'sym' || f[0]!.e % 2 === 0) return t;
  }
  const syms = [...polySymbols(n.rf.n), ...polySymbols(n.rf.d)];
  return syms.length === 1 ? sym(syms[0]!) : t;
}

/** Trois cas a > 0 | a < 0 | a = 0, chacun revérifié sous son hypothèse. */
function branchOn(c: Condition, prev: Statement, next: Statement, ctx: Ctx): StepCheck | null {
  const p = parseStatement(c.of[0]!);
  if (!p.ok || p.s.k !== 'expr') return null;
  const v = branchVariable(p.s.e, ctx);
  const branches: Branch[] = [];
  for (const kind of ['positive', 'negative', 'zero'] as const) {
    const when = conditionOn(kind, v);
    if (decide(when, ctx.facts) === 'false') continue;
    const hyps = [...ctx.hyps, when];
    const sub: Ctx = { ...ctx, hyps, facts: factsFrom(hyps, ctx.sorts), branches: false };
    branches.push({ when: [when], check: checkStatements(prev, next, sub) });
  }
  if (branches.length < 2) return branches[0]?.check ?? null;
  const first = branches[0]!.check;
  if (branches.every((b) => b.check.status === first.status && b.check.relation === first.relation))
    return first;
  return {
    status: 'conditional',
    relation: 'none',
    proof: 'none',
    explanation: { code: 'depends_on_sign', params: { of: clip(termText(v)) } },
    branches,
  };
}

/* ------------------------------ 2–3. expressions ------------------------------ */

function hasSymbols(t: Term): boolean {
  return statementSymbols({ k: 'expr', e: t }).size > 0;
}

function checkExpressions(a: Term, b: Term, ctx: Ctx): StepCheck {
  const nopts = { sorts: ctx.sorts };
  const eq = compareTerms(a, b, nopts);
  if (eq === 'equal') {
    const nb = normalize(b, nopts);
    const isNumber = nb.ok && rfRat(nb.rf) !== null;
    return hasSymbols(a) || !isNumber
      ? valid('identity', 'identity')
      : valid('evaluation', 'evaluation');
  }
  // valeur approchée d'une constante
  if (!hasSymbols(a) && !hasSymbols(b)) {
    const approx = approximationCheck(a, b, ctx);
    if (approx) return approx;
  }
  // contre-exemple exact
  const w = expressionWitness(a, b, ctx);
  if (w && !w.at) return invalid('wrong_value', { got: w.got!, expected: w.expected! });
  if (w) return invalid('counterexample', w);
  if (eq === 'different') return invalid('not_identical');
  // dernier recours : valeurs approchées très différentes (preuve par échantillons)
  const s = floatWitness(a, b, ctx);
  if (s) return invalid('counterexample', s, 'sampled');
  return unknownCheck('no_proof');
}

function expressionWitness(a: Term, b: Term, ctx: Ctx): Record<string, string> | null {
  const names = [...statementSymbols({ k: 'or', items: [{ rel: '=', l: a, r: b }] })];
  for (const env of sampleEnvs(names, ctx.facts, `${termKey(a)}|${termKey(b)}`, 12)) {
    let va: Rat | null;
    let vb: Rat | null;
    try {
      va = evalExact(a, env, { sorts: ctx.sorts });
      vb = evalExact(b, env, { sorts: ctx.sorts });
    } catch {
      continue;
    }
    if (va && vb && !Q.eq(va, vb))
      return { at: envText(env), got: termText(num(vb)), expected: termText(num(va)) };
  }
  return null;
}
function floatWitness(a: Term, b: Term, ctx: Ctx): Record<string, string> | null {
  const names = [...statementSymbols({ k: 'or', items: [{ rel: '=', l: a, r: b }] })];
  for (const env of sampleEnvs(names, ctx.facts, `f|${termKey(a)}|${termKey(b)}`, 12)) {
    const fenv: Record<string, number> = {};
    for (const [k, v] of Object.entries(env)) fenv[k] = Q.ratToNumber(v);
    const x = evalFloat(a, fenv);
    const y = evalFloat(b, fenv);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (Math.abs(x - y) > 1e-6 * Math.max(1, Math.abs(x), Math.abs(y))) return { at: envText(env) };
  }
  return null;
}
function envText(env: Readonly<Record<string, Rat | Term>>): string {
  return Object.entries(env)
    .map(([k, v]) => `${k} = ${termText('k' in v ? (v as Term) : num(v as Rat))}`)
    .join(', ');
}

/** « √2 → 1,414 » : jamais une identité ; une valeur approchée si la précision est donnée. */
function approximationCheck(a: Term, b: Term, ctx: Ctx): StepCheck | null {
  const nb = normalize(b, { sorts: ctx.sorts });
  if (!nb.ok) return null;
  const approx = rfRat(nb.rf);
  if (!approx || approx.d === 1n) return null;
  const places = decimalPlaces(approx);
  if (places === null) return null;
  const na = normalize(a, { sorts: ctx.sorts });
  if (!na.ok || rfRat(na.rf) !== null) return null;
  const exact = radicalOf(na.rf);
  let rounds: boolean | null = null;
  let proof: StepCheck['proof'] = 'exact';
  if (exact) rounds = Q.isRoundingOf(exact, approx, places);
  else {
    const v = polyFloat(na.rf.n) / polyFloat(na.rf.d);
    if (!Number.isFinite(v)) return null;
    const half = 0.5 * 10 ** -places;
    const gap = Math.abs(v - Q.ratToNumber(approx));
    if (Math.abs(gap - half) < 1e-12) return null;
    rounds = gap < half;
    proof = 'sampled';
  }
  const wanted = ctx.approximation?.places;
  if (rounds && wanted !== undefined && places >= wanted)
    return valid('approximation', 'approximation', { places: String(places) }, proof);
  if (rounds) return invalid('approximation_not_exact', { places: String(places) }, proof);
  return invalid('bad_approximation', { places: String(places) }, proof);
}
function decimalPlaces(q: Rat): number | null {
  let d = q.d;
  let twos = 0;
  let fives = 0;
  while (d % 2n === 0n) {
    d /= 2n;
    twos++;
  }
  while (d % 5n === 0n) {
    d /= 5n;
    fives++;
  }
  return d === 1n ? Math.max(twos, fives) : null;
}
/** a + b√p exactement (une seule racine), sinon null. */
function radicalOf(r: RatFn): Q.Radical | null {
  const d = polyRat(r.d);
  if (d === null) return null;
  const terms = [...r.n.t.values()];
  if (terms.length !== 1 || terms[0]!.m.length !== 1) return null;
  const f = terms[0]!.m[0]!;
  if (f.a.kind !== 'root' || f.e !== 1) return null;
  return Q.radical(Q.div(terms[0]!.c, d)!, BigInt(f.a.prime!));
}

/* ------------------------------ « S ≈ v » ------------------------------ */

/**
 * « S ≈ v » (lot 3 bis) : v est-il une valeur approchée de S ? S est une
 * constante (« √2 ≈ 1,41 »), ou une grandeur que `from` détermine (« x² = 2 »,
 * x > 0 : x = √2). Décidé EXACTEMENT pour un rationnel ou c√p, par valeurs
 * approchées sinon (preuve `sampled`, jamais exacte).
 *
 * Précision : celle de v (« 1,41 » : au centième), ou celle demandée
 * (`approximation.places`) si elle est plus fine — « 8 » pour un arrondi au
 * dixième doit valoir 8,0. Une grandeur à plusieurs valeurs possibles (± √2)
 * n'est décidée que si toutes s'accordent.
 */
export function checkApproximation(
  from: Statement | null,
  l: Term,
  approx: Term,
  opts: AlgebraCheckContext,
): StepCheck {
  const sorts = opts.sorts ?? {};
  const na = normalize(approx, { sorts });
  const a = na.ok ? rfRat(na.rf) : null;
  const own = a ? decimalPlaces(a) : null;
  if (!a || own === null) return unsupportedCheck('approximation_not_decimal');
  const places = Math.max(own, opts.approximation?.places ?? 0);
  const exacts = exactValuesOf(from, l, opts);
  if (!exacts?.length) return unknownCheck('no_exact_value');
  const verdicts = exacts.map((e) => roundingVerdict(e, a, places, sorts));
  if (verdicts.some((v) => v === null)) return unknownCheck('approximation_undecided');
  const vs = verdicts as RoundingVerdict[];
  const proof = vs.some((v) => v.proof === 'sampled') ? 'sampled' : 'exact';
  const params = { places: String(places) };
  if (vs.every((v) => v.kind === 'rounds'))
    return valid('approximation', 'approximation', params, proof);
  if (vs.some((v) => v.kind === 'rounds')) return unknownCheck('several_values');
  // « 1,4 » quand on demande le centième : un arrondi, mais pas à la précision voulue
  if (own < places) {
    const coarse = exacts.map((e) => roundingVerdict(e, a, own, sorts));
    if (coarse.every((v) => v?.kind === 'rounds'))
      return invalid(
        'approximation_too_coarse',
        { places: String(own), wanted: String(places) },
        proof,
      );
  }
  if (vs.every((v) => v.kind === 'truncates')) return invalid('truncated', params, proof);
  return invalid('bad_approximation', params, proof);
}

interface RoundingVerdict {
  kind: 'rounds' | 'truncates' | 'wrong';
  proof: 'exact' | 'sampled';
}

/** Valeurs exactes de `l` : constante, ou solutions de `from` (hypothèses respectées). */
function exactValuesOf(from: Statement | null, l: Term, opts: AlgebraCheckContext): Term[] | null {
  const sorts = opts.sorts ?? {};
  if (statementSymbols({ k: 'expr', e: l }).size === 0) return [l];
  if (l.k !== 'sym' || !from) return null;
  const name = l.n;
  const out: Term[] = [];
  for (const p of partsOf(from)) {
    if (p.rel !== '=') return null;
    const n = normalize(sub(p.l, p.r), { sorts });
    if (!n.ok) return null;
    const used = new Set([...polySymbols(n.rf.n), ...polySymbols(n.rf.d)]);
    if (used.size !== 1 || !used.has(name)) return null;
    const sol = univariateSolutions(n.rf, name);
    if (!sol?.complete) return null;
    out.push(...sol.roots);
  }
  // hypothèses sur la grandeur (une longueur est positive)
  const facts = factsFrom([], sorts);
  const own = (opts.hypotheses ?? []).filter((h) => {
    const t = h.of.length === 1 ? parseStatement(h.of[0]!) : null;
    return !!t?.ok && t.s.k === 'expr' && t.s.e.k === 'sym' && t.s.e.n === name;
  });
  const kept: Term[] = [];
  for (const t of out) {
    if (own.some((h) => decide(conditionOn(h.kind, t), facts) === 'false')) continue;
    if (!kept.some((k) => compareTerms(k, t, { sorts }) === 'equal')) kept.push(t);
  }
  return kept;
}

/** `a` arrondi (ou tronqué) de la valeur exacte `e` à `places` chiffres ? null : indécis. */
function roundingVerdict(e: Term, a: Rat, places: number, sorts: Sorts): RoundingVerdict | null {
  const ne = normalize(e, { sorts });
  if (!ne.ok) return null;
  const q = rfRat(ne.rf);
  const r = q ? null : radicalOf(ne.rf);
  const v = q ? Q.ratToNumber(q) : polyFloat(ne.rf.n) / polyFloat(ne.rf.d);
  if (!Number.isFinite(v)) return null;
  const exact = !!(q || r);
  /** signe de (e − b) ; null si trop proche pour décider en flottants */
  const cmpTo = (b: Rat): -1 | 0 | 1 | null => {
    if (q) return Q.cmp(q, b);
    if (r) return Q.radCmpRat(r, b);
    const d = v - Q.ratToNumber(b);
    if (Math.abs(d) < 1e-12 * Math.max(1, Math.abs(v))) return null;
    return d > 0 ? 1 : -1;
  };
  const unit = Q.rat(1n, 10n ** BigInt(places));
  const half = Q.rat(1n, 2n * 10n ** BigInt(places));
  const lo = cmpTo(Q.sub(a, half));
  const hi = cmpTo(Q.add(a, half));
  if (lo === null || hi === null) return null;
  const proof = exact ? 'exact' : 'sampled';
  if (lo >= 0 && hi <= 0) return { kind: 'rounds', proof };
  // tronquer au lieu d'arrondir : a ≤ e < a + 10^−p (e ≥ 0), a − 10^−p < e ≤ a (e < 0)
  const sign = cmpTo(Q.ZERO);
  if (sign === null) return null;
  const inside =
    sign >= 0
      ? cmpTo(a) !== -1 && cmpTo(Q.add(a, unit)) === -1
      : cmpTo(a) !== 1 && cmpTo(Q.sub(a, unit)) === 1;
  return { kind: inside ? 'truncates' : 'wrong', proof };
}

/* ------------------------------ 2–3. relations ------------------------------ */

interface Part {
  rel: Rel;
  l: Term;
  r: Term;
}
/** Relations d'un énoncé (toutes, clauses d'un ensemble aplaties). */
const partsOf = (s: Statement): Part[] => relationsIn(s);

/** Vérité d'une relation en un point (valeurs exactes) ; null si indécidable. */
function partTruth(p: Part, env: Readonly<Record<string, Term>>, ctx: Ctx): boolean | null {
  let e: RatFn;
  try {
    e = toRatFn(substitute(sub(p.l, p.r), env as Record<string, Term>), { sorts: ctx.sorts });
  } catch (err) {
    if (err instanceof NormalFail)
      return err.code === 'division_by_zero' || err.code === 'undefined_value' ? false : null;
    throw err;
  }
  // les membres eux-mêmes doivent être définis
  for (const side of [p.l, p.r]) {
    const n = normalize(substitute(side, env as Record<string, Term>), { sorts: ctx.sorts });
    if (!n.ok) return n.code === 'too_big' ? null : false;
  }
  if (polyHasOpaque(e.n) || polyHasOpaque(e.d)) return null;
  const sign = constSign(e);
  if (sign === null) return null;
  switch (p.rel) {
    case '=':
      return sign === 0;
    case '<':
      return sign < 0;
    case '>':
      return sign > 0;
    case '≤':
      return sign <= 0;
    case '≥':
      return sign >= 0;
  }
}
function constSign(e: RatFn): -1 | 0 | 1 | null {
  if (polyIsZero(e.n)) return 0;
  const q = rfRat(e);
  if (q) return Q.sign(q);
  if (!polyIsConstant(e.n) || !polyIsConstant(e.d)) return null;
  const v = polyFloat(e.n) / polyFloat(e.d);
  if (!Number.isFinite(v) || Math.abs(v) < 1e-9) return null;
  return v > 0 ? 1 : -1;
}
function stmtTruth(s: Statement, env: Readonly<Record<string, Term>>, ctx: Ctx): boolean | null {
  // un ensemble (moteur v23) : vrai si l'une de ses clauses l'est (toutes ses relations vraies)
  if (s.k === 'set') {
    let undecided = false;
    for (const c of s.clauses) {
      let all: boolean | null = true;
      for (const p of c) {
        const t = partTruth(p, env, ctx);
        if (t === false) {
          all = false;
          break;
        }
        if (t === null) all = null;
      }
      if (all === true) return true;
      if (all === null) undecided = true;
    }
    return undecided ? null : false;
  }
  let undecided = false;
  for (const p of partsOf(s)) {
    const t = partTruth(p, env, ctx);
    if (t === true) return true;
    if (t === null) undecided = true;
  }
  return undecided ? null : false;
}

function checkRelations(prev: Statement, next: Statement, ctx: Ctx): StepCheck {
  const names = [...new Set([...statementSymbols(prev), ...statementSymbols(next)])];
  // équation trigonométrique en radians (moteur v24) : solutions comparées exactement
  if (ctx.trig && !Object.keys(ctx.angles).length) {
    const t = trigCheck(prev, next, names, ctx);
    if (t) return t;
    // sur un intervalle, l'étude sur ℝ ne dirait pas la même chose
    if (ctx.trig.domain) return unknownCheck('no_proof');
  }
  if (names.length === 0) return checkConstantRelations(prev, next, ctx);
  if (names.length === 1) {
    const dom = ctx.angles[names[0]!];
    if (dom) {
      const a = angleStudy(prev, next, names[0]!, dom, ctx);
      if (a) return a;
    }
    const u = univariate(prev, next, names[0]!, ctx);
    if (u) return u;
  }
  // même relation, chaque membre réécrit sans changer de valeur (lot 6.43 : l'étude ne
  // sait pas trouver les zéros de « e^(3x + 1) − 1 ÷ e^(x − 2) », mais 1 ÷ e^(x − 2) et
  // e^(2 − x) ont la même forme normale)
  const same = sameMembers(prev, next, ctx);
  if (same) return same;
  if (prev.k === 'rel' && next.k === 'rel') {
    const d = byDivisibility(prev, next, ctx);
    if (d) return d;
  }
  const w = multivariateWitness(prev, next, names, ctx);
  if (w) return invalid('counterexample', { at: w });
  return unknownCheck('no_proof');
}

/**
 * Même relation, membres de même valeur deux à deux : les deux lignes sont équivalentes —
 * seulement si aucun dénominateur ne peut s'annuler (sinon le domaine change : « x^7 ÷ x^3 »
 * → « x^4 » demande x^3 ≠ 0, c'est byDivisibility qui le dit). e^u ne s'annule jamais.
 */
function sameMembers(prev: Statement, next: Statement, ctx: Ctx): StepCheck | null {
  if (prev.k !== 'rel' || next.k !== 'rel' || prev.rel !== next.rel) return null;
  const o = { sorts: ctx.sorts };
  for (const t of [prev.l, prev.r, next.l, next.r]) {
    const n = normalize(t, o);
    if (!n.ok) return null;
    if (polyRat(n.rf.d) === null && isNonzero(n.rf.d, ctx.facts) !== 'true') return null;
  }
  if (compareTerms(prev.l, next.l, o) === 'equal' && compareTerms(prev.r, next.r, o) === 'equal')
    return valid('equivalent', 'equivalent');
  return null;
}

/** Étude exacte d'une équation trigonométrique (moteur v24, kernel/trig.ts). */
function trigCheck(prev: Statement, next: Statement, names: string[], ctx: Ctx): StepCheck | null {
  const x = ctx.goal?.target;
  if (!x || !names.includes(x)) return null;
  const params = new Set(names.filter((n) => n !== x));
  if ([...params].some((p) => !ctx.facts.integer.has(p))) return null;
  const domain = ctx.trig?.domain ? trigDomainOf(ctx.trig.domain, x, ctx.sorts) : null;
  if (ctx.trig?.domain && !domain) return null;
  const c = compareTrig(prev, next, x, params, domain, ctx.sorts);
  if (!c) return null;
  if (c.kind === 'equal') return valid('equivalent', 'equivalent');
  const at = { at: trigAtText(x, c.at) };
  if (c.kind === 'extra') return valid('implies', 'extra_solutions', at);
  return invalid(c.kind === 'lost' ? 'lost_solution' : 'counterexample', at);
}

function checkConstantRelations(prev: Statement, next: Statement, ctx: Ctx): StepCheck {
  const p = stmtTruth(prev, {}, ctx);
  const n = stmtTruth(next, {}, ctx);
  if (p === null || n === null) return unknownCheck('no_proof');
  if (p && !n) return invalid('false_statement');
  if (p && n) return valid('equivalent', 'equivalent');
  return n ? valid('implies', 'vacuous') : valid('equivalent', 'equivalent');
}

/* ---- une inconnue : étude exacte complète ---- */

interface Point {
  t: Term;
  v: number;
}

function criticalPoints(
  s: Statement,
  x: string,
  ctx: Ctx,
): { pts: Point[]; complete: boolean } | null {
  const pts: Point[] = [];
  let complete = true;
  for (const p of partsOf(s)) {
    const n = normalize(sub(p.l, p.r), { sorts: ctx.sorts });
    if (!n.ok) return null;
    for (const poly of [n.rf.n, n.rf.d]) {
      if (polyIsConstant(poly)) continue;
      const sol = univariateSolutions({ n: poly, d: UNIT }, x);
      if (!sol) return null;
      complete &&= sol.complete;
      for (const t of sol.roots) pts.push({ t, v: evalFloat(t, {}) });
    }
    // bords du domaine (√u, ln u) : la relation peut y cesser d'être définie sans que
    // sa forme normale s'annule (√x < 3 n'a pas de sens pour x < 0)
    for (const side of [p.l, p.r]) pts.push(...domainEdges(side, x, ctx));
  }
  return { pts, complete };
}

/** Où l'argument d'une racine ou d'un logarithme qui dépend de x s'annule. */
function domainEdges(t: Term, x: string, ctx: Ctx): Point[] {
  const out: Point[] = [];
  const walk = (u: Term): void => {
    switch (u.k) {
      case 'add':
      case 'mul':
        u.c.forEach(walk);
        return;
      case 'div':
      case 'pow':
      case 'binom':
        walk(u.a);
        walk(u.b);
        return;
      case 'fn': {
        walk(u.a);
        if ((u.f !== 'sqrt' && u.f !== 'ln') || !symbolsOf(u.a).has(x)) return;
        const n = normalize(u.a, { sorts: ctx.sorts });
        if (!n.ok || polyIsConstant(n.rf.n)) return;
        // un bord introuvable ne change rien à l'étude (comportement d'avant)
        const sol = univariateSolutions({ n: n.rf.n, d: UNIT }, x);
        sol?.roots.forEach((r) => out.push({ t: r, v: evalFloat(r, {}) }));
        return;
      }
      default:
        return;
    }
  };
  walk(t);
  return out;
}
const UNIT: Poly = { t: new Map([['', { m: [], c: Q.ONE }]]) };

/** Un rationnel simple strictement entre a et b (a < b). */
function between(a: number, b: number, integer: boolean): Rat | null {
  if (integer) {
    const k = Math.floor(a) + 1;
    return k < b ? Q.rat(k) : null;
  }
  if (b - a > 1) {
    const k = Math.floor(a) + 1;
    if (k < b) return Q.rat(k);
  }
  for (let d = 2; d <= 4096; d *= 2) {
    const n = Math.floor(a * d) + 1;
    if (n / d < b && n / d > a) return Q.rat(n, d);
  }
  return null;
}

function univariate(prev: Statement, next: Statement, x: string, ctx: Ctx): StepCheck | null {
  const cp = criticalPoints(prev, x, ctx);
  const cn = criticalPoints(next, x, ctx);
  if (!cp || !cn) return null;
  const complete = cp.complete && cn.complete;
  // points critiques distincts, ordonnés
  const all: Point[] = [];
  for (const p of [...cp.pts, ...cn.pts]) {
    if (!Number.isFinite(p.v)) return null;
    if (all.some((q) => Math.abs(q.v - p.v) < 1e-12 && compareTerms(q.t, p.t) === 'equal'))
      continue;
    if (all.some((q) => Math.abs(q.v - p.v) < 1e-9)) return null; // trop proches pour départager
    all.push(p);
  }
  all.sort((a, b) => a.v - b.v);
  const integer = ctx.facts.integer.has(x);
  const tests: Term[] = [];
  const pushRat = (r: Rat | null): void => {
    if (r) tests.push(num(r));
  };
  if (!all.length) [0, 1, -1].forEach((k) => tests.push(num(k)));
  else {
    pushRat(between(all[0]!.v - 2, all[0]!.v, integer));
    for (let i = 0; i < all.length; i++) {
      tests.push(all[i]!.t);
      if (i + 1 < all.length) pushRat(between(all[i]!.v, all[i + 1]!.v, integer));
    }
    pushRat(between(all[all.length - 1]!.v, all[all.length - 1]!.v + 2, integer));
  }
  let undecided = false;
  let converse: Term | null = null;
  for (const t of tests) {
    if (!inDomain(x, t, ctx)) continue;
    const env = { [x]: t };
    const p = stmtTruth(prev, env, ctx);
    const n = stmtTruth(next, env, ctx);
    if (p === null || n === null) {
      undecided = true;
      continue;
    }
    if (p && !n)
      return invalid(
        prev.k === 'or' || (prev.k === 'rel' && prev.rel === '=')
          ? 'lost_solution'
          : 'counterexample',
        {
          at: `${x} = ${termText(canon(t))}`,
        },
      );
    if (n && !p) converse ??= t;
  }
  if (!complete || undecided) return null;
  // une solution de la ligne d'arrivée qui n'en était pas une au départ (lot 4 : message)
  if (converse)
    return valid('implies', 'extra_solutions', { at: `${x} = ${termText(canon(converse))}` });
  return valid('equivalent', 'equivalent');
}

/** Le point respecte-t-il les hypothèses sur x (signe, entier, expressions non nulles) ? */
function inDomain(x: string, t: Term, ctx: Ctx): boolean {
  const v = evalFloat(t, {});
  const s = ctx.facts.sign.get(x);
  if (s) {
    const sg = Math.abs(v) < 1e-12 ? 0 : v > 0 ? 1 : -1;
    if ((sg > 0 && !s.pos) || (sg < 0 && !s.neg) || (sg === 0 && !s.zero)) return false;
  }
  if (ctx.facts.integer.has(x)) {
    const n = normalize(t);
    const q = n.ok ? rfRat(n.rf) : null;
    if (!q || !Q.isInteger(q)) return false;
  }
  for (const h of ctx.facts.polys) {
    let r: RatFn;
    try {
      r = toRatFn(substitute(polyToTerm(h.p), { [x]: t }));
    } catch {
      return false;
    }
    const sg = constSign(r);
    if (sg === null) continue;
    if ((sg > 0 && !h.s.pos) || (sg < 0 && !h.s.neg) || (sg === 0 && !h.s.zero)) return false;
  }
  return true;
}

/* ---- plusieurs lettres : rapport de formes normales ---- */

/** E > 0 (ou ≥ 0, ou = 0) : polynôme orienté, dénominateur constant positif ; null sinon. */
function oriented(
  s: { rel: Rel; l: Term; r: Term },
  ctx: Ctx,
): { p: Poly; d: Poly; rel: '=' | '<' | '≤' } | null {
  const [l, r, rel] =
    s.rel === '>'
      ? [s.r, s.l, '<' as const]
      : s.rel === '≥'
        ? [s.r, s.l, '≤' as const]
        : [s.l, s.r, s.rel];
  // l < r  ⟺  r − l > 0
  const n = normalize(sub(r, l), { sorts: ctx.sorts });
  if (!n.ok) return null;
  return { p: n.rf.n, d: n.rf.d, rel };
}

function byDivisibility(
  prev: { k: 'rel'; rel: Rel; l: Term; r: Term },
  next: { k: 'rel'; rel: Rel; l: Term; r: Term },
  ctx: Ctx,
): StepCheck | null {
  const a = oriented(prev, ctx);
  const b = oriented(next, ctx);
  if (!a || !b) return null;
  if ((a.rel === '=') !== (b.rel === '=')) return null;
  if (a.rel === '=') {
    // même dénominateur des deux côtés (à une constante près) : même domaine, aucune
    // condition nouvelle (« CM = V₁/V₀ » → « CM = V₁ ÷ V₀ ») ; sinon chacun, une fois
    const ratio = polyDivExact(a.d, b.d);
    const sameDomain = ratio !== null && polyIsConstant(ratio);
    const domain = (sameDomain ? [] : [a.d, b.d])
      .filter((d) => polyRat(d) === null)
      .map((d) => conditionOn('nonzero', polyToTerm(d)))
      .filter((c, i, all) => all.findIndex((o) => o.of[0] === c.of[0]) === i);
    const open = domain.filter((c) => decide(c, ctx.facts) !== 'true');
    const done = (r: StepCheck): StepCheck =>
      open.length && r.status === 'valid' ? { ...r, status: 'conditional', conditions: open } : r;
    if (polyIsZero(b.p)) return polyIsZero(a.p) ? done(valid('equivalent', 'equivalent')) : null;
    // départ toujours vrai (0 = 0) : une équation non triviale n'en découle pas (lot 3 :
    // 0 ÷ p = 0 passait pour un rapport constant, et « 0 = 0 → x = y » pour une équivalence)
    if (polyIsZero(a.p)) return null;
    const k = polyDivExact(a.p, b.p);
    if (k) {
      if (polyIsConstant(k)) return done(valid('equivalent', 'equivalent'));
      const c = conditionOn('nonzero', polyToTerm(k));
      const d = decide(c, ctx.facts);
      if (d === 'true') return done(valid('equivalent', 'equivalent'));
      // une solution perdue là où le facteur s'annule ?
      const w = multivariateWitness(prev, next, [...polySymbols(a.p), ...polySymbols(b.p)], ctx);
      if (w) return invalid('lost_solution', { at: w });
      return {
        ...valid('equivalent', 'equivalent'),
        status: 'conditional',
        conditions: [c, ...open].filter(
          (x, i, all) => all.findIndex((o) => o.kind === x.kind && o.of[0] === x.of[0]) === i,
        ),
      };
    }
    const k2 = polyDivExact(b.p, a.p);
    if (k2 && !polyIsConstant(k2)) {
      const c = conditionOn('nonzero', polyToTerm(k2));
      if (decide(c, ctx.facts) === 'true') return done(valid('equivalent', 'equivalent'));
      return done(valid('implies', 'may_add_solutions'));
    }
    return null;
  }
  // inéquations : dénominateurs constants seulement
  const da = polyRat(a.d);
  const db = polyRat(b.d);
  if (da === null || db === null) return null;
  const pa = polyScale(a.p, Q.rat(Q.sign(da)));
  const pb = polyScale(b.p, Q.rat(Q.sign(db)));
  if (polyIsZero(pa) || polyIsZero(pb)) return null;
  const k = polyDivExact(pa, pb);
  if (!k) return null;
  const kt = polyToTerm(k);
  const sgn = signOfK(k, ctx);
  if (sgn === 0) {
    // signe inconnu : un cas par signe
    if (!ctx.branches) return null;
    return branchOn(conditionOn('positive', kt), prev, next, ctx);
  }
  if (sgn < 0) {
    const w = multivariateWitness(prev, next, [...polySymbols(pa), ...polySymbols(pb)], ctx);
    return w ? invalid('sign_not_flipped', { at: w, factor: clip(termText(kt)) }) : null;
  }
  // même sens : comparer la largeur
  if (a.rel === b.rel) return valid('equivalent', 'equivalent');
  if (a.rel === '<' && b.rel === '≤') return valid('implies', 'weakened');
  const w = multivariateWitness(prev, next, [...polySymbols(pa), ...polySymbols(pb)], ctx);
  return w ? invalid('counterexample', { at: w }) : null;
}
function signOfK(k: Poly, ctx: Ctx): -1 | 0 | 1 {
  const d = decide(conditionOn('positive', polyToTerm(k)), ctx.facts);
  if (d === 'true') return 1;
  const n = decide(conditionOn('negative', polyToTerm(k)), ctx.facts);
  return n === 'true' ? -1 : 0;
}

/* ---- plusieurs lettres : contre-exemple exact ---- */

/**
 * Cherche un point où la ligne de départ est vraie et la suivante fausse :
 * paramètres tirés au hasard (déterministe), puis étude exacte en une inconnue.
 */
function multivariateWitness(
  prev: Statement,
  next: Statement,
  names: string[],
  ctx: Ctx,
): string | null {
  const uniq = [...new Set(names)].sort();
  const target = ctx.goal?.target;
  // d'abord les lettres de la ligne de départ : la résoudre en l'une d'elles la rend vraie
  // (lot 3 : « S = 100 → S = N(P + D) » n'était jamais réfuté, S venant après D, N, P)
  const inPrev = [...statementSymbols(prev)].sort();
  const order = [...new Set([...inPrev, ...uniq])].filter((n) => uniq.includes(n));
  // l'inconnue de la question d'abord, puis les lettres de la ligne de départ (lot 3 bis :
  // « r = d ÷ 2 → A = π × d² » ne se réfute qu'en résolvant en d, pas en A)
  const unknowns = (
    target && uniq.includes(target) ? [target, ...order.filter((n) => n !== target)] : order
  ).slice(0, 4);
  for (const x of unknowns) {
    const params = uniq.filter((n) => n !== x);
    for (const env of sampleEnvs(params, ctx.facts, `${statementText(prev)}|${x}`, 6)) {
      if (ctx.now() > ctx.deadline) return null;
      const map: Record<string, Term> = {};
      for (const [k, v] of Object.entries(env)) map[k] = num(v);
      const p = mapStatement(prev, (t) => substitute(t, map));
      const n = mapStatement(next, (t) => substitute(t, map));
      const sub: Ctx = { ...ctx, branches: false };
      const r = univariate(p, n, x, sub);
      if (r?.status === 'invalid') {
        const at = r.explanation?.params?.at ?? '';
        return [envText(env), at].filter(Boolean).join(', ');
      }
    }
  }
  return null;
}

/* ------------------------------ matrices ------------------------------ */

type Mat = Rat[][];
function matMul(a: Mat, b: Mat): Mat {
  return a.map((row) =>
    b[0]!.map((_, j) => row.reduce((s, x, k) => Q.add(s, Q.mul(x, b[k]![j]!)), Q.ZERO)),
  );
}
function matAdd(a: Mat, b: Mat): Mat {
  return a.map((row, i) => row.map((x, j) => Q.add(x, b[i]![j]!)));
}
const scal = (k: Rat, n: number): Mat =>
  Array.from({ length: n }, (_, i) => Array.from({ length: n }, (_, j) => (i === j ? k : Q.ZERO)));
function matEval(t: Term, env: Record<string, Mat>, n: number): Mat | null {
  switch (t.k) {
    case 'num':
      return scal(t.v, n);
    case 'sym':
      return env[t.n] ?? null;
    case 'add': {
      let acc: Mat | null = scal(Q.ZERO, n);
      for (const c of t.c) {
        const m = matEval(c, env, n);
        if (!m || !acc) return null;
        acc = matAdd(acc, m);
      }
      return acc;
    }
    case 'mul': {
      let acc: Mat | null = scal(Q.ONE, n);
      for (const c of t.c) {
        const m = matEval(c, env, n);
        if (!m || !acc) return null;
        acc = matMul(acc, m);
      }
      return acc;
    }
    case 'pow': {
      if (t.b.k !== 'num' || !Q.isInteger(t.b.v) || t.b.v.n < 0n || t.b.v.n > 16n) return null;
      const base = matEval(t.a, env, n);
      if (!base) return null;
      let acc = scal(Q.ONE, n);
      for (let i = 0n; i < t.b.v.n; i++) acc = matMul(acc, base);
      return acc;
    }
    default:
      return null;
  }
}

/** Matrices : forme canonique (jamais AB = BA), contre-exemple exact avec des matrices 2 × 2. */
function checkMatrixStep(prev: Statement, next: Statement, ctx: Ctx): StepCheck {
  if (prev.k !== 'expr' || next.k !== 'expr') return unsupportedCheck('matrix_relation');
  if (sameTerm(prev.e, next.e, ctx.sorts)) return valid('identity', 'identity');
  const names = [...statementSymbols(prev), ...statementSymbols(next)];
  const fixed: Mat[] = [
    [
      [1, 2],
      [0, 1],
    ],
    [
      [0, 1],
      [1, 0],
    ],
    [
      [2, 0],
      [1, 3],
    ],
    [
      [1, -1],
      [2, 0],
    ],
  ].map((m) => m.map((row) => row.map((v) => Q.rat(v))));
  const env: Record<string, Mat> = {};
  [...new Set(names)].sort().forEach((n, i) => {
    env[n] = ctx.sorts[n] === 'matrix' ? fixed[i % fixed.length]! : scal(Q.rat(i + 2), 2);
  });
  const a = matEval(prev.e, env, 2);
  const b = matEval(next.e, env, 2);
  if (!a || !b) return unknownCheck('no_proof');
  const differs = a.some((row, i) => row.some((x, j) => !Q.eq(x, b[i]![j]!)));
  return differs ? invalid('not_commutative', { at: 'matrices 2 × 2' }) : unknownCheck('no_proof');
}
